import {
  CheckCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
  LoadingOutlined
} from "@ant-design/icons"
import { App, Button, Modal, Select, Upload } from "antd"
import type { RcFile } from "antd/es/upload/interface"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"

const { Dragger } = Upload

interface QueueItem {
  id: string
  file: File
  status: "queued" | "uploading" | "done" | "failed"
}

export interface UploadProjectOption {
  id: string
  name: string
  recipientLabel: string
}

interface UploadAndShareModalProps {
  open: boolean
  onClose: () => void
  accept?: string
  onUpload: (file: File, note: string, projectId: string) => Promise<boolean>
  onAllUploaded: () => void
  /** Single-project context: fixed, always known. */
  project?: UploadProjectOption
  /** All-projects context: nothing is chosen yet — "Do not silently submit
   * into whichever project was last opened" means the customer must pick
   * one here before the upload area even appears. */
  projectOptions?: UploadProjectOption[]
}

let nextId = 0

export function UploadAndShareModal({
  open,
  onClose,
  accept,
  onUpload,
  onAllUploaded,
  project,
  projectOptions
}: UploadAndShareModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [items, setItems] = useState<QueueItem[]>([])
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [chosenProjectId, setChosenProjectId] = useState<string | null>(null)

  const activeProject =
    project ?? projectOptions?.find((p) => p.id === chosenProjectId) ?? null

  const addFiles = (files: RcFile[]) => {
    setItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `upload-${nextId++}`,
        file,
        status: "queued" as const
      }))
    ])
    return false
  }

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id))
  }

  const uploadOne = async (item: QueueItem, projectId: string) => {
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, status: "uploading" } : i))
    )
    const ok = await onUpload(item.file, note, projectId)
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id ? { ...i, status: ok ? "done" : "failed" } : i
      )
    )
    return ok
  }

  const handleSubmit = async () => {
    if (!activeProject) return
    const pending = items.filter(
      (item) => item.status === "queued" || item.status === "failed"
    )
    if (pending.length === 0) return
    setSubmitting(true)
    const results = await Promise.all(
      pending.map((item) => uploadOne(item, activeProject.id))
    )
    setSubmitting(false)

    if (results.every(Boolean)) {
      message.success(t("portal.upload.success"))
      onAllUploaded()
      reset()
      onClose()
    } else {
      message.error(t("portal.upload.partialFailure"))
    }
  }

  const reset = () => {
    setItems([])
    setNote("")
    setChosenProjectId(null)
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }

  const hasFailed = items.some((item) => item.status === "failed")
  const allDone =
    items.length > 0 && items.every((item) => item.status === "done")

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={t("portal.upload.title")}
      footer={null}
      maskClosable={!submitting}
      closable={!submitting}
    >
      <div className="flex flex-col gap-4">
        {projectOptions && (
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              {t("portal.upload.projectLabel")}
            </label>
            <Select
              className="w-full"
              placeholder={t("portal.upload.chooseProject")}
              value={chosenProjectId ?? undefined}
              onChange={setChosenProjectId}
              options={projectOptions.map((p) => ({
                value: p.id,
                label: p.name
              }))}
              disabled={submitting}
            />
          </div>
        )}

        {activeProject && (
          <>
            <Dragger
              multiple
              accept={accept}
              showUploadList={false}
              beforeUpload={(_file, fileList) => addFiles(fileList)}
              disabled={submitting}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="text-sm text-gray-600">
                {t("portal.upload.dropHint")}
              </p>
            </Dragger>

            {items.length > 0 && (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm"
                  >
                    <span className="truncate text-gray-800">
                      {item.file.name}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {item.status === "uploading" && <LoadingOutlined />}
                      {item.status === "done" && (
                        <CheckCircleFilled className="text-green-600" />
                      )}
                      {item.status === "failed" && (
                        <span className="flex items-center gap-1 text-red-600">
                          <ExclamationCircleFilled />
                          {t("portal.upload.failed")}
                        </span>
                      )}
                      {item.status !== "uploading" &&
                        item.status !== "done" && (
                          <button
                            type="button"
                            onClick={() => removeItem(item.id)}
                            aria-label={t("portal.upload.remove")}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <CloseOutlined />
                          </button>
                        )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              {t("portal.upload.sharingWith", {
                recipient: activeProject.recipientLabel,
                project: activeProject.name
              })}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                {t("portal.upload.noteLabel")}
              </label>
              <TextArea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("portal.upload.notePlaceholder")}
                disabled={submitting}
              />
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button onClick={handleClose} disabled={submitting}>
            {t("portal.upload.cancel")}
          </Button>
          <Button
            type="primary"
            onClick={() => void handleSubmit()}
            loading={submitting}
            disabled={!activeProject || items.length === 0 || allDone}
          >
            {hasFailed ? t("portal.upload.retry") : t("portal.upload.submit")}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
